<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import { shiftsApi } from '@/api/shifts';
import type { AxiosError } from 'axios';

const { t } = useI18n();

const emit = defineEmits(['close', 'success']);

const props = withDefaults(
  defineProps<{
    shiftId: number;
    mode: 'suspend' | 'unsuspend';
    closeButtonText?: string;
  }>(),
  { closeButtonText: 'Cerrar' },
);

const isSuspend = computed(() => props.mode === 'suspend');

const { handleSubmit } = useForm();

const { mutate, isError, isPending, isSuccess } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => shiftsApi.update({ id: props.shiftId, suspended: isSuspend.value }),
    onSuccess: () => {
      emit('success');
      emit('close');
    },
  },
);

const onSubmit = handleSubmit(() => mutate());
</script>
<template>
  <v-card
    class="w-100 pa-4"
    flat
  >
    <v-card-title>
      {{ isSuspend ? t('shiftSuspendForm.suspendTitle') : t('shiftSuspendForm.unsuspendTitle') }}
    </v-card-title>
    <v-form
      v-if="!isPending && !isSuccess"
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-card flat>
        <v-card-text>
          {{ isSuspend
            ? t('shiftSuspendForm.suspendConfirmation')
            : t('shiftSuspendForm.unsuspendConfirmation') }}
        </v-card-text>
        <v-card-actions>
          <v-btn
            :text="props.closeButtonText"
            @click="$emit('close')"
          />
          <v-spacer />
          <v-btn
            :color="isSuspend ? 'grey-darken-1' : 'primary'"
            type="submit"
            :disabled="isPending || isSuccess"
          >
            {{ isSuspend
              ? t('shiftSuspendForm.suspendSubmit')
              : t('shiftSuspendForm.unsuspendSubmit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <v-col
      v-if="isPending"
      class="pb-20"
    >
      <v-row class="justify-center d-flex">
        <v-progress-circular
          class="mx-auto"
          indeterminate
          color="primary"
        />
      </v-row>
    </v-col>
    <v-col v-if="isSuccess">
      <v-row class="justify-center d-flex">
        <v-icon
          icon="mdi-check-circle"
          color="primary"
          size="50"
        />
      </v-row>
      <v-row class="justify-center mt-8 d-flex">
        <p>
          {{ isSuspend
            ? t('shiftSuspendForm.suspendSuccess')
            : t('shiftSuspendForm.unsuspendSuccess') }}
        </p>
      </v-row>
    </v-col>
    <v-alert
      v-if="isError"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{ isSuspend
        ? t('shiftSuspendForm.suspendError')
        : t('shiftSuspendForm.unsuspendError') }}
    </v-alert>
  </v-card>
</template>
